import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Grid,
  useToast,
  Spinner,
  IconButton,
  GridItem,
  useColorMode,
  Text,
  Card,
  CardBody,
  Skeleton,
  Tooltip,
  Badge,
} from '@chakra-ui/react';
import { MdRefresh, MdInfoOutline, MdPhone, MdTimer, MdAttachMoney, MdTrendingUp } from 'react-icons/md';
import axios from '../utils/axiosConfig';
import Navigation from '../components/Navigation';
import MonthlySpendChart from '../components/MonthlySpendChart';
import CallDirectionChart from '../components/CallDirectionChart';
import { Helmet } from 'react-helmet';

const DashboardPage = () => {
  const { colorMode } = useColorMode();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalCalls: 0,
    totalMinutes: 0,
    totalSpend: 0,
    avgCostPerCall: 0,
    trend: 'neutral',
    monthlySpend: [],
    directionData: {
      inbound: 0,
      outbound: 0,
    },
  });

  const toast = useToast();

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      console.log('Fetching dashboard data...');
      const response = await axios.get('/api/protected/stats');
      console.log('API Response:', response.data);
      
      if (response.data.success) {
        console.log('Setting dashboard data:', response.data.data);
        setDashboardData(response.data.data);
      } else {
        console.log('API returned error:', response.data.message);
        throw new Error(response.data.message || 'Failed to fetch stats');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Full error object:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch dashboard data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Dashboard mounted, fetching data...');
    fetchDashboardData();
  }, []);

  console.log('Current dashboardData:', dashboardData);

  const StatCard = ({ title, value, helpText, trend, isLoading }) => (
    <Card
      bg={colorMode === 'light' ? 'white' : 'gray.800'}
      borderRadius="lg"
      boxShadow="sm"
      _hover={{ boxShadow: 'md' }}
      transition="all 0.2s"
    >
      <CardBody>
        <Stat>
          <Flex justify="space-between" align="center">
            <StatLabel color={colorMode === 'light' ? 'gray.600' : 'gray.400'}>
              {title}
            </StatLabel>
            {trend && (
              <Badge
                colorScheme={
                  trend === 'increase'
                    ? 'red'
                    : trend === 'decrease'
                    ? 'green'
                    : 'gray'
                }
                fontSize="xs"
              >
                {trend === 'increase'
                  ? '↑ Increasing'
                  : trend === 'decrease'
                  ? '↓ Decreasing'
                  : '→ Stable'}
              </Badge>
            )}
          </Flex>
          <Skeleton isLoaded={!isLoading}>
            <StatNumber fontSize="2xl" mt={2}>
              {value}
            </StatNumber>
          </Skeleton>
          <StatHelpText color={colorMode === 'light' ? 'gray.600' : 'gray.400'}>
            {helpText}
          </StatHelpText>
        </Stat>
      </CardBody>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Dashboard | AI Call System</title>
        <meta name="description" content="View your call statistics and analytics" />
      </Helmet>

      <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
        <Navigation />
        <Box p={{ base: 4, md: 8 }} flex="1" overflow="auto" width="100%">
          <Flex
            justify="space-between"
            align={{ base: "start", md: "center" }}
            mb={6}
            flexDirection={{ base: "column", md: "row" }}
            gap={4}
          >
            <Flex align="center" gap={2}>
              <Heading size={{ base: "md", md: "lg" }} color={colorMode === 'light' ? 'gray.800' : 'white'}>Dashboard</Heading>
              <Tooltip label="Last updated data">
                <IconButton
                  icon={<MdInfoOutline />}
                  aria-label="Info"
                  variant="ghost"
                  size="sm"
                />
              </Tooltip>
            </Flex>
            <IconButton
              icon={<MdRefresh />}
              aria-label="Refresh Dashboard"
              onClick={fetchDashboardData}
              isLoading={loading}
              colorScheme="brand"
              variant="ghost"
              alignSelf={{ base: "flex-end", md: "center" }}
            />
          </Flex>

          <Grid
            templateColumns={{
              base: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            }}
            gap={{ base: 4, md: 6 }}
            mb={6}
          >
            <StatCard
              title="Total Calls"
              value={dashboardData.totalCalls?.toLocaleString() || '0'}
              helpText="All time total calls"
              isLoading={loading}
            />
            <StatCard
              title="Total Minutes"
              value={`${dashboardData.totalMinutes || '0'} mins`}
              helpText="All time total call duration"
              isLoading={loading}
            />
            <StatCard
              title="Total Spend"
              value={`$${dashboardData.totalSpend || '0'}`}
              helpText="Total cost of all calls"
              isLoading={loading}
            />
            <StatCard
              title="Avg Cost/Call"
              value={`$${dashboardData.avgCostPerCall || '0'}`}
              helpText="Average cost per call"
              trend={dashboardData.trend}
              isLoading={loading}
            />
          </Grid>

          <Grid
            templateColumns={{
              base: '1fr',
              lg: 'repeat(2, 1fr)',
            }}
            gap={{ base: 4, md: 8 }}
            mb={{ base: 4, md: 8 }}
          >
            <GridItem>
              <Card
                bg={colorMode === 'light' ? 'white' : 'gray.800'}
                borderRadius="lg"
                boxShadow="sm"
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
                height="100%"
              >
                <CardBody>
                  <Heading size={{ base: "sm", md: "md" }} mb={4}>
                    Inbound vs Outbound Calls
                  </Heading>
                  <Skeleton isLoaded={!loading} >
                    <CallDirectionChart
                      directionData={dashboardData.directionData}
                    />
                  </Skeleton>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card
                bg={colorMode === 'light' ? 'white' : 'gray.800'}
                borderRadius="lg"
                boxShadow="sm"
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
                height="100%"
              >
                <CardBody>
                  <Heading size={{ base: "sm", md: "md" }} mb={4}>
                    Monthly Spend
                  </Heading>
                  <Skeleton isLoaded={!loading} >
                    <MonthlySpendChart
                      monthlySpendData={dashboardData.monthlySpend}
                    />
                  </Skeleton>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        </Box>
      </Flex>
    </>
  );
};

export default DashboardPage;
